'use client';

import { Label } from '@savvy-kit/components/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@savvy-kit/components/select';
import { isNil } from 'es-toolkit';
import { useField, useFormikContext } from 'formik';

type FormikSelectFieldProps = {
  readonly name: string;
  readonly label?: string;
  readonly options: readonly { value: string; label: string }[];
  readonly required?: boolean;
  readonly placeholder?: string;
  readonly disabled?: boolean;
  readonly className?: string;
  // Runs after the field value is set — for callers that need to clear a dependent field.
  readonly onValueChange?: (value: string) => void;
};

/*
 * Formik counterpart to `react-hook-form-components/select-field`, for the forms
 * that migrated off MUI's `<TextField select>` + `MenuItem`.
 */
export function FormikSelectField({
  name,
  label,
  options,
  required = false,
  placeholder,
  disabled,
  className,
  onValueChange,
}: FormikSelectFieldProps) {
  const [field, meta, helpers] = useField<string>(name);
  const { isSubmitting } = useFormikContext();

  const hasError = !isSubmitting && meta.touched && !isNil(meta.error) && meta.error !== '';

  // Base UI only shows the placeholder when the value is `null`; an empty string
  // renders a blank trigger. Mirrors the react-hook-form SelectField.
  const value = field.value === '' || isNil(field.value) ? null : String(field.value);

  return (
    <div className={className}>
      {label !== undefined && (
        <Label htmlFor={name} className="mb-2">
          {label}
          {required ? <span className="text-destructive ml-1">*</span> : null}
        </Label>
      )}
      <Select
        value={value}
        items={[...options]}
        disabled={disabled}
        onValueChange={(next) => {
          helpers.setValue(String(next));
          onValueChange?.(String(next));
        }}
      >
        <SelectTrigger
          id={name}
          className="w-full"
          onBlur={field.onBlur}
          aria-invalid={hasError}
          aria-describedby={hasError ? `${name}-error` : undefined}
        >
          <SelectValue placeholder={placeholder ?? 'None'} />
        </SelectTrigger>
        {/*
         * Every caller is a form that migrated off MUI, and most render inside a MUI
         * Dialog (z-index 1300). The popup portals to <body>, so the kit default of
         * z-50 puts it *behind* the dialog — visible but not clickable. 1500 matches
         * the old SelectProps.MenuProps, as in add-household-member-dialog.
         */}
        <SelectContent positionerClassName="z-[1500]">
          {options.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {hasError && (
        <p id={`${name}-error`} className="text-destructive mt-1 text-sm">
          {meta.error}
        </p>
      )}
    </div>
  );
}
